import React, { useEffect, useRef, useState } from "react";

import { Site } from "../../api/sites";

type ContextMenuState = {
    x: number;
    y: number;
    siteId: number | null;
};

type Props = {
    sites: Site[];
    selectedId: number | null;
    onSelect: (id: number) => void;
    onAdd: () => void;
    onManage: (siteId?: number) => void;
    onManageSsh: () => void;
    onReorder: (siteIds: number[]) => Promise<void>;
};

const SiteNavigator: React.FC<Props> = ({
    sites,
    selectedId,
    onSelect,
    onAdd,
    onManage,
    onManageSsh,
    onReorder,
}) => {
    const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
    const [orderedSites, setOrderedSites] = useState(sites);
    const [draggedSiteId, setDraggedSiteId] = useState<number | null>(null);
    const [isSavingOrder, setIsSavingOrder] = useState(false);
    const contextMenuRef = useRef<HTMLDivElement>(null);
    const orderedSitesRef = useRef(sites);
    const didDropRef = useRef(false);
    const lastDragTargetIdRef = useRef<number | null>(null);
    const contextSite =
        sites.find((site) => site.id === contextMenu?.siteId) ?? null;

    useEffect(() => {
        if (draggedSiteId != null || isSavingOrder) return;
        orderedSitesRef.current = sites;
        setOrderedSites(sites);
    }, [draggedSiteId, isSavingOrder, sites]);

    useEffect(() => {
        if (!contextMenu) return;

        const closeMenu = (event: MouseEvent) => {
            if (!contextMenuRef.current?.contains(event.target as Node)) {
                setContextMenu(null);
            }
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") setContextMenu(null);
        };

        document.addEventListener("mousedown", closeMenu);
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            document.removeEventListener("mousedown", closeMenu);
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, [contextMenu]);

    const openContextMenu = (
        event: React.MouseEvent,
        siteId: number | null
    ) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            x: Math.min(event.clientX, window.innerWidth - 210),
            y: Math.min(event.clientY, window.innerHeight - 170),
            siteId,
        });
    };

    const runMenuAction = (action: () => void) => {
        setContextMenu(null);
        action();
    };

    const updatePreviewOrder = (nextSites: Site[]) => {
        orderedSitesRef.current = nextSites;
        setOrderedSites(nextSites);
    };

    const moveSite = (siteId: number, targetSiteId: number) => {
        const current = orderedSitesRef.current;
        const fromIndex = current.findIndex((site) => site.id === siteId);
        const targetIndex = current.findIndex((site) => site.id === targetSiteId);
        if (fromIndex < 0 || targetIndex < 0 || fromIndex === targetIndex) return current;

        const next = [...current];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(targetIndex, 0, moved);
        updatePreviewOrder(next);
        return next;
    };

    const saveOrder = async (nextSites: Site[]) => {
        if (isSavingOrder) return;
        const previousIds = sites.map((site) => site.id).join(",");
        const nextIds = nextSites.map((site) => site.id).join(",");
        if (previousIds === nextIds) return;

        setIsSavingOrder(true);
        try {
            await onReorder(nextSites.map((site) => site.id));
        } catch {
            updatePreviewOrder(sites);
        } finally {
            setIsSavingOrder(false);
        }
    };

    const handleKeyboardReorder = (
        event: React.KeyboardEvent,
        siteId: number
    ) => {
        if (!event.shiftKey || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) return;
        event.preventDefault();
        const current = orderedSitesRef.current;
        const index = current.findIndex((site) => site.id === siteId);
        const targetIndex = index + (event.key === "ArrowUp" ? -1 : 1);
        if (index < 0 || targetIndex < 0 || targetIndex >= current.length) return;
        const next = moveSite(siteId, current[targetIndex].id);
        void saveOrder(next);
    };

    return (
        <aside
            className="site-navigator"
            aria-label="배포 경로"
            onContextMenu={(event) => openContextMenu(event, null)}
        >
            <div className="site-navigator-header">
                <strong>배포 경로</strong>
                <span>{sites.length}</span>
                <button
                    type="button"
                    aria-label="경로 등록"
                    title="경로 등록"
                    onClick={onAdd}
                >
                    +
                </button>
            </div>

            <div
                className={`site-navigator-list${draggedSiteId != null ? " is-reordering" : ""}`}
                role="listbox"
                aria-label="등록된 배포 경로"
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                    event.preventDefault();
                    didDropRef.current = true;
                    setDraggedSiteId(null);
                    void saveOrder(orderedSitesRef.current);
                }}
            >
                {orderedSites.length === 0 ? (
                    <div className="site-navigator-empty">등록된 경로가 없습니다.</div>
                ) : (
                    orderedSites.map((site) => {
                        const selected = site.id === selectedId;
                        const sshConfigured = Boolean(site.sshHost && site.sshUsername);
                        const cacheState = site.compileCacheStatus?.toLowerCase();
                        const showCacheState = cacheState && cacheState !== "unsupported";
                        return (
                            <button
                                key={site.id}
                                type="button"
                                role="option"
                                aria-selected={selected}
                                className={`site-navigator-item${selected ? " is-active" : ""}${draggedSiteId === site.id ? " is-dragging" : ""}`}
                                title={`${site.localPath}\n${site.homePath}\n드래그하여 순서 변경`}
                                draggable={!isSavingOrder}
                                onDragStart={(event) => {
                                    didDropRef.current = false;
                                    lastDragTargetIdRef.current = null;
                                    setDraggedSiteId(site.id);
                                    event.dataTransfer.effectAllowed = "move";
                                    event.dataTransfer.setData("text/plain", String(site.id));
                                }}
                                onDragEnter={() => {
                                    if (lastDragTargetIdRef.current === site.id) return;
                                    lastDragTargetIdRef.current = site.id;
                                    if (draggedSiteId != null) moveSite(draggedSiteId, site.id);
                                }}
                                onDragEnd={() => {
                                    setDraggedSiteId(null);
                                    if (!didDropRef.current) updatePreviewOrder(sites);
                                    didDropRef.current = false;
                                    lastDragTargetIdRef.current = null;
                                }}
                                onKeyDown={(event) => handleKeyboardReorder(event, site.id)}
                                onClick={() => onSelect(site.id)}
                                onContextMenu={(event) => openContextMenu(event, site.id)}
                                >
                                <span
                                    className={`site-navigator-status${
                                        sshConfigured ? " is-ssh-ready" : ""
                                    }`}
                                    aria-hidden="true"
                                />
                                <span className="site-navigator-copy">
                                    <strong>{site.text}</strong>
                                    <small>
                                        {sshConfigured
                                            ? `${site.sshUsername}@${site.sshHost}:${site.sshPort ?? 22}`
                                            : site.localPath}
                                    </small>
                                </span>
                                <span
                                    className="site-navigator-grip"
                                    aria-hidden="true"
                                    title="드래그하여 순서 변경"
                                >
                                    <i /><i /><i /><i /><i /><i />
                                </span>
                                {showCacheState && (
                                    <span
                                        className={`site-navigator-cache-status is-${cacheState}`}
                                        title={site.compileCacheMessage || "컴파일 캐시 상태"}
                                        aria-label={site.compileCacheMessage || "컴파일 캐시 상태"}
                                    >
                                        CACHE
                                    </span>
                                )}
                            </button>
                        );
                    })
                )}
            </div>
            <span className="site-navigator-order-status" aria-live="polite">
                {isSavingOrder ? "경로 순서 저장 중" : ""}
            </span>

            {contextMenu && (
                <div
                    ref={contextMenuRef}
                    className="site-navigator-context"
                    role="menu"
                    aria-label={contextSite ? `${contextSite.text} 경로 작업` : "경로 작업"}
                    style={{ left: contextMenu.x, top: contextMenu.y }}
                    onContextMenu={(event) => event.preventDefault()}
                >
                    {contextSite && (
                        <button
                            type="button"
                            role="menuitem"
                            onClick={() =>
                                runMenuAction(() => onSelect(contextSite.id))
                            }
                        >
                            배포 대상으로 선택
                        </button>
                    )}
                    <button
                        type="button"
                        role="menuitem"
                        onClick={() =>
                            runMenuAction(() => onManage(contextSite?.id))
                        }
                    >
                        {contextSite ? "이 경로 관리" : "전체 경로 관리"}
                    </button>
                    <button
                        type="button"
                        role="menuitem"
                        onClick={() => runMenuAction(onManageSsh)}
                    >
                        SSH 관리
                    </button>
                    <div role="separator" />
                    <button
                        type="button"
                        role="menuitem"
                        onClick={() => runMenuAction(onAdd)}
                    >
                        새 경로 등록
                    </button>
                </div>
            )}
        </aside>
    );
};

export default SiteNavigator;
